import { useState, useEffect, useCallback, useRef } from 'react';
import MatchRoundService from '../services/MatchRoundService';
import ChatService from '../services/ChatService';
import { resolveAvatarUrl } from '../utils/api';
import triggerHaptic from '../utils/haptics';

/**
 * Lädt und verwaltet die Match-Runde ("Deine 6 für heute").
 *
 * Gegenstück zu useProfileLoader, aber mit anderem Vertrag: keine Paginierung,
 * kein Nachladen, kein Infinite Scroll — die Runde hat sechs Karten und ein Ende.
 * useProfileLoader bleibt daneben bestehen und bedient weiterhin die Suche.
 */

// Dieselbe Normalisierung wie in useProfileLoader: das Backend liefert Skills je
// nach Endpoint unter drei verschiedenen Namen.
const normalizeProfiles = (profiles = []) => {
  if (!Array.isArray(profiles)) return [];

  return profiles.map(profile => {
    const offered = profile.tenant_skills_OfferedSkills || profile.offeredSkills || profile.offers || [];
    const sought = profile.tenant_skills_SoughtSkills || profile.soughtSkills || profile.seeks || [];

    const toNames = list => (Array.isArray(list)
      ? list.map(skill => (typeof skill === 'string' ? skill : skill.name || '')).filter(Boolean)
      : []);

    const normalizedOffers = toNames(offered);
    const normalizedSeeks = toNames(sought);

    return {
      ...profile,
      offeredSkills: normalizedOffers,
      soughtSkills: normalizedSeeks,
      offers: normalizedOffers,
      seeks: normalizedSeeks,
      name: profile.displayName || profile.name,
      bio: profile.bio || profile.aboutMe || '',
      aboutMe: profile.aboutMe || profile.bio || '',
      image: resolveAvatarUrl(profile.image || profile.avatar || '', 'card'),
      avatar: resolveAvatarUrl(profile.avatar || profile.image || '', 'thumb'),
      matchScore: profile.matchScore || 0
    };
  });
};

export const useMatchRound = ({ authUser, enabled = true }) => {
  const [profiles, setProfiles] = useState([]);
  const [round, setRound] = useState(null);
  const [meta, setMeta] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [error, setError] = useState(null);

  // Verhindert doppelte Ziehungen, wenn schnell hintereinander getippt wird —
  // erste Verteidigungslinie vor Server-Sperre und Rate-Limit.
  const drawInFlightRef = useRef(false);

  /**
   * Reichert die Karten um den Verbindungsstatus an (vernetzt / Anfrage offen).
   * Nicht blockierend: die Karten sind schon sichtbar, der Knopf aktualisiert
   * sich nach.
   */
  const attachConnectionStatus = useCallback(async (list) => {
    if (list.length === 0) return list;
    try {
      const ids = list.map(profile => profile.userId || profile.id).filter(Boolean);
      const statuses = await ChatService.getConnectionStatusBatch(ids);
      return list.map(profile => ({
        ...profile,
        connectionStatus: statuses?.[profile.userId || profile.id] || 'none'
      }));
    } catch {
      // Der Status ist Beiwerk — ohne ihn zeigt die Karte einfach "Vernetzen".
      return list;
    }
  }, []);

  const applyResult = useCallback(async (result) => {
    const normalized = normalizeProfiles(result.profiles || []);
    setProfiles(normalized);
    setRound(result.round || null);
    setMeta(result.meta || {});
    setError(null);

    const enriched = await attachConnectionStatus(normalized);
    setProfiles(enriched);
  }, [attachConnectionStatus]);

  /** Die aktuelle Runde laden — verbraucht kein Kontingent. */
  const loadRound = useCallback(async () => {
    if (!enabled || !authUser?.id) return;
    setIsLoading(true);
    try {
      const result = await MatchRoundService.getRound();
      await applyResult(result);
    } catch (loadError) {
      setError(loadError?.message || 'Deine Runde konnte nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, authUser?.id, applyResult]);

  /**
   * Neue Runde ziehen. Wird sowohl vom Knopf als auch von der Pull-Geste genutzt.
   * @returns {Promise<boolean>} true, wenn tatsächlich gezogen wurde
   */
  const drawNextRound = useCallback(async () => {
    if (!enabled || !authUser?.id || drawInFlightRef.current) return false;

    drawInFlightRef.current = true;
    setIsDrawing(true);
    try {
      const result = await MatchRoundService.drawNextRound();
      await applyResult(result);
      triggerHaptic('success');
      return true;
    } catch (drawError) {
      if (drawError?.limitReached) {
        // Kein Fehlerzustand, sondern ein normaler Endpunkt des Tages: nur den
        // Zähler aktualisieren, damit die Schlusskarte den Countdown zeigt.
        if (drawError.round) setRound(drawError.round);
        return false;
      }
      if (drawError?.conflict) {
        // Eine parallele Anfrage war schneller — die gültige Runde nachladen.
        await loadRound();
        return false;
      }
      setError(drawError?.message || 'Die neue Runde konnte nicht geladen werden.');
      return false;
    } finally {
      drawInFlightRef.current = false;
      setIsDrawing(false);
    }
  }, [enabled, authUser?.id, applyResult, loadRound]);

  /**
   * "Später zeigen". Die Karte verschwindet sofort aus der laufenden Runde,
   * es rückt aber nichts nach — die Runde wird kürzer. Nachrücken würde die
   * Begrenzung aushebeln und die Geste zum Nachschub-Hebel machen.
   */
  const snoozeProfile = useCallback(async (targetUserId) => {
    if (!targetUserId) return;
    setProfiles(current => current.filter(
      profile => (profile.userId || profile.id) !== targetUserId
    ));
    try {
      await MatchRoundService.snooze(targetUserId);
    } catch {
      // Die Karte bleibt bis zum nächsten Laden weg. Sie zurückspringen zu
      // lassen wäre verwirrender als der stille Fehlschlag.
    }
  }, []);

  /**
   * Nach "Blockieren": Karte sofort aus der Runde nehmen. Der Server filtert
   * Blockierte beim naechsten Laden ohnehin heraus; hier geht es nur darum,
   * dass die Karte nicht bis zum Neuladen stehen bleibt. Wie beim Snooze
   * rueckt nichts nach.
   */
  const removeProfile = useCallback((targetUserId) => {
    if (!targetUserId) return;
    setProfiles(current => current.filter(
      profile => (profile.userId || profile.id) !== targetUserId
    ));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    if (authUser?.id) loadRound();
  }, [enabled, authUser?.id, loadRound]);

  return {
    profiles,
    round,
    meta,
    isLoading,
    isDrawing,
    error,
    loadRound,
    drawNextRound,
    snoozeProfile,
    removeProfile,
    // Die Runde ist verbraucht, wenn nichts mehr gezogen werden darf.
    canDraw: round?.canDrawNow ?? true
  };
};

export default useMatchRound;
